import { useEffect, useMemo, useState } from "react";

/** Gleich wie Backend matchFormat.js — ADL-Bogen. */
export const SINGLES_PAIRINGS = [
  [0, 0], [1, 1], [2, 2], [3, 3],
  [0, 1], [1, 0], [2, 3], [3, 2],
  [0, 3], [1, 2], [2, 1], [3, 0],
  [0, 2], [1, 3], [2, 0], [3, 1],
];

export const DOUBLES_PAIRINGS = [
  { home: [0, 1], away: [0, 1] },
  { home: [2, 3], away: [2, 3] },
];

const ROUND_LABELS = ["Runde 1", "Runde 2", "Runde 3", "Runde 4"];

function nameOf(p) {
  if (!p) return "—";
  return p.nickname ? `${p.name} „${p.nickname}“` : p.name || "—";
}

/** Gleicher Gesamtscore wie auf der Spielerkarte (Mittel SCO/FIN/NER). */
function overallOf(p) {
  if (!p) return null;
  if (p.overall != null && p.overall !== "") {
    const n = Math.round(Number(p.overall));
    return Number.isFinite(n) ? n : null;
  }
  const vals = [p.scoring, p.finishing, p.nerves].map((v) => Number(v) || 0);
  if (!vals.some((v) => v > 0)) return null;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function nameWithScore(p) {
  const name = nameOf(p);
  const score = overallOf(p);
  return score != null ? `${name} · ${score}` : name;
}

function slotLabel(i) {
  return `E${i + 1}`;
}

/**
 * Papier-ähnlicher ADL-Spielberichtsbogen.
 * mode: "blank" | "filled"
 * editable: eigene Vier (H1–H4 oder G1–G4 je nach isHome) umsortieren
 */
const Spielberichtsbogen = ({
  homeName = "Heim",
  awayName = "Gast",
  homePlayers = [],
  awayPlayers = [],
  squad = [],
  isHome = true,
  games = null,
  competitionLabel = "Begegnung",
  seasonNumber = null,
  matchday = null,
  scoreHome = null,
  scoreAway = null,
  editable = false,
  saving = false,
  onSaveLineup,
  hint = "",
  /** Zusätzliche Aktionen direkt neben „Aufstellung speichern“ (z. B. Anpfiff-Buttons). */
  afterSaveActions = null,
}) => {
  const ownKeys = (isHome ? homePlayers : awayPlayers).map((p) => p.playerKey).filter(Boolean);
  const [draft, setDraft] = useState(["", "", "", ""]);

  useEffect(() => {
    const keys = [...ownKeys];
    while (keys.length < 4) keys.push("");
    setDraft(keys.slice(0, 4));
  }, [ownKeys.join("|")]);

  const homeFour = useMemo(() => {
    if (isHome && editable) {
      return draft.map((key) => squad.find((p) => p.playerKey === key) || homePlayers.find((p) => p.playerKey === key) || null);
    }
    return [0, 1, 2, 3].map((i) => homePlayers[i] || null);
  }, [isHome, editable, draft, squad, homePlayers]);

  const awayFour = useMemo(() => {
    if (!isHome && editable) {
      return draft.map((key) => squad.find((p) => p.playerKey === key) || awayPlayers.find((p) => p.playerKey === key) || null);
    }
    return [0, 1, 2, 3].map((i) => awayPlayers[i] || null);
  }, [isHome, editable, draft, squad, awayPlayers]);

  const resultsBySlot = useMemo(() => {
    const map = {};
    (games || []).forEach((g) => {
      if (g?.slot) map[g.slot] = g;
    });
    return map;
  }, [games]);

  const filled = Boolean(games?.length);
  const ownSideLabel = isHome ? "Heim (H1–H4)" : "Gast (G1–G4)";

  const setPos = (idx, key) => {
    setDraft((prev) => {
      const next = [...prev];
      const swapAt = next.indexOf(key);
      if (swapAt >= 0) next[swapAt] = prev[idx];
      next[idx] = key;
      return next;
    });
  };

  const save = async () => {
    if (!onSaveLineup) return;
    if (draft.filter(Boolean).length < 4) return;
    await onSaveLineup(draft);
  };

  const scoreCell = (slot) => {
    const g = resultsBySlot[slot];
    if (!g || !filled) return "";
    return `${g.legsHome ?? g.legsWonHome ?? 0}:${g.legsAway ?? g.legsWonAway ?? 0}`;
  };

  const winSide = (slot) => {
    const g = resultsBySlot[slot];
    if (!g || !filled) return null;
    const h = Number(g.legsHome ?? g.legsWonHome ?? 0);
    const a = Number(g.legsAway ?? g.legsWonAway ?? 0);
    if (h === a) return "draw";
    return h > a ? "home" : "away";
  };

  const singlesRounds = [0, 1, 2, 3].map((round) =>
    SINGLES_PAIRINGS.slice(round * 4, round * 4 + 4).map((pair, j) => {
      const idx = round * 4 + j;
      return { idx, hi: pair[0], ai: pair[1], slot: slotLabel(idx) };
    })
  );

  const options = (squad.length ? squad : isHome ? homePlayers : awayPlayers)
    .filter((p) => !p.loanedOut)
    .slice()
    .sort((a, b) => (overallOf(b) || 0) - (overallOf(a) || 0));

  const labelOwn = editable ? nameWithScore : nameOf;
  const labelOpp = editable ? nameWithScore : nameOf;

  return (
    <section className="rounded-sm border-2 border-gray-800 bg-[#f7f4ea] text-gray-900 shadow-lg overflow-hidden font-sans">
      <div className="bg-gray-900 text-amber-100 px-4 py-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Spielberichtsbogen</div>
          <h3 className="text-xl font-black tracking-tight">{competitionLabel}</h3>
          {seasonNumber != null || matchday != null ? (
            <div className="mt-1 text-lg md:text-2xl font-black text-yellow-300">
              Saison {seasonNumber ?? "—"}
              <span className="mx-2 opacity-50">·</span>
              Spieltag {matchday ?? "—"}
            </div>
          ) : null}
        </div>
        <div className="text-right text-sm">
          <div className="font-bold">{homeName}</div>
          <div className="opacity-60 text-xs">vs</div>
          <div className="font-bold">{awayName}</div>
        </div>
      </div>

      {hint ? <p className="px-4 pt-3 text-sm text-amber-900/90">{hint}</p> : null}

      <div className="p-4 grid md:grid-cols-2 gap-4">
        <div className="border border-gray-700 bg-white/70">
          <div className="bg-gray-800 text-white text-xs uppercase tracking-wide px-3 py-1.5 font-bold">
            Heim · {homeName}
          </div>
          <ul className="divide-y divide-gray-300">
            {[0, 1, 2, 3].map((i) => (
              <li key={`H${i + 1}`} className="grid grid-cols-[2.5rem_1fr] items-center min-h-[2.75rem]">
                <span className="px-2 text-xs font-black bg-gray-100 border-r border-gray-300 h-full flex items-center justify-center">
                  H{i + 1}
                </span>
                {editable && isHome ? (
                  <select
                    className="w-full bg-transparent px-2 py-2 text-sm font-semibold"
                    value={draft[i] || ""}
                    onChange={(e) => setPos(i, e.target.value)}
                    title="Name · Gesamtscore (Mittel Scoring/Finishing/Nerven)"
                  >
                    <option value="">— wählen —</option>
                    {options.map((p) => (
                      <option key={p.playerKey} value={p.playerKey}>
                        {nameWithScore(p)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="px-2 py-2 text-sm font-semibold truncate">
                    {labelOwn(homeFour[i])}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-gray-700 bg-white/70">
          <div className="bg-gray-800 text-white text-xs uppercase tracking-wide px-3 py-1.5 font-bold">
            Gast · {awayName}
          </div>
          <ul className="divide-y divide-gray-300">
            {[0, 1, 2, 3].map((i) => (
              <li key={`G${i + 1}`} className="grid grid-cols-[2.5rem_1fr] items-center min-h-[2.75rem]">
                <span className="px-2 text-xs font-black bg-gray-100 border-r border-gray-300 h-full flex items-center justify-center">
                  G{i + 1}
                </span>
                {editable && !isHome ? (
                  <select
                    className="w-full bg-transparent px-2 py-2 text-sm font-semibold"
                    value={draft[i] || ""}
                    onChange={(e) => setPos(i, e.target.value)}
                    title="Name · Gesamtscore (Mittel Scoring/Finishing/Nerven)"
                  >
                    <option value="">— wählen —</option>
                    {options.map((p) => (
                      <option key={p.playerKey} value={p.playerKey}>
                        {nameWithScore(p)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="px-2 py-2 text-sm font-semibold truncate">
                    {labelOpp(awayFour[i])}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {editable ? (
        <div className="px-4 pb-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={saving || draft.filter(Boolean).length < 4}
              onClick={save}
              className="px-4 py-2 rounded bg-gray-900 text-amber-50 text-sm font-bold disabled:opacity-50"
            >
              {saving ? "Speichern …" : `Aufstellung ${ownSideLabel} speichern`}
            </button>
            {afterSaveActions}
          </div>
          <span className="text-xs opacity-70 block">
            H1 spielt das erste Einzel gegen G1. Zahl hinter dem Namen = Gesamtscore.
          </span>
        </div>
      ) : null}

      <div className="px-4 pb-4 space-y-4">
        {singlesRounds.map((rows, round) => (
          <div key={ROUND_LABELS[round]} className="border border-gray-700 bg-white/80 overflow-hidden">
            <div className="bg-gray-200 px-3 py-1 text-xs font-black uppercase tracking-wide border-b border-gray-700">
              Einzel · {ROUND_LABELS[round]}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide bg-gray-100 border-b border-gray-300">
                  <th className="p-2 text-left w-10">#</th>
                  <th className="p-2 text-left">Heim</th>
                  <th className="p-2 text-center w-16">Legs</th>
                  <th className="p-2 text-right">Gast</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const side = winSide(row.slot);
                  return (
                    <tr key={row.slot} className="border-t border-gray-200">
                      <td className="p-2 text-xs font-bold opacity-60">{row.slot}</td>
                      <td className={`p-2 font-semibold ${side === "home" ? "text-emerald-800" : ""}`}>
                        <span className="text-[10px] opacity-50 mr-1">H{row.hi + 1}</span>
                        {editable ? nameWithScore(homeFour[row.hi]) : nameOf(homeFour[row.hi])}
                      </td>
                      <td className="p-2 text-center font-black tabular-nums border-x border-gray-200 bg-[#fffdf6]">
                        {scoreCell(row.slot) || <span className="opacity-30">·</span>}
                      </td>
                      <td className={`p-2 font-semibold text-right ${side === "away" ? "text-emerald-800" : ""}`}>
                        {editable ? nameWithScore(awayFour[row.ai]) : nameOf(awayFour[row.ai])}
                        <span className="text-[10px] opacity-50 ml-1">G{row.ai + 1}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        <div className="border border-gray-700 bg-white/80 overflow-hidden">
          <div className="bg-gray-200 px-3 py-1 text-xs font-black uppercase tracking-wide border-b border-gray-700">
            Doppel
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide bg-gray-100 border-b border-gray-300">
                <th className="p-2 text-left w-10">#</th>
                <th className="p-2 text-left">Heim</th>
                <th className="p-2 text-center w-16">Legs</th>
                <th className="p-2 text-right">Gast</th>
              </tr>
            </thead>
            <tbody>
              {DOUBLES_PAIRINGS.map((pair, i) => {
                const slot = `D${i + 1}`;
                const side = winSide(slot);
                const homePair = `${nameOf(homeFour[pair.home[0]])} / ${nameOf(homeFour[pair.home[1]])}`;
                const awayPair = `${nameOf(awayFour[pair.away[0]])} / ${nameOf(awayFour[pair.away[1]])}`;
                return (
                  <tr key={slot} className="border-t border-gray-200">
                    <td className="p-2 text-xs font-bold opacity-60">{slot}</td>
                    <td className={`p-2 font-semibold ${side === "home" ? "text-emerald-800" : ""}`}>{homePair}</td>
                    <td className="p-2 text-center font-black tabular-nums border-x border-gray-200 bg-[#fffdf6]">
                      {scoreCell(slot) || <span className="opacity-30">·</span>}
                    </td>
                    <td className={`p-2 font-semibold text-right ${side === "away" ? "text-emerald-800" : ""}`}>
                      {awayPair}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="border-2 border-gray-900 bg-white flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="text-xs uppercase tracking-wide font-bold opacity-70">Spiele Gesamt</div>
          <div className="text-3xl font-black tabular-nums">
            {filled && scoreHome != null && scoreAway != null ? (
              <>
                {scoreHome}
                <span className="mx-2 opacity-40">:</span>
                {scoreAway}
              </>
            ) : (
              <span className="opacity-30">— : —</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Spielberichtsbogen;
